if (!customElements.get('card-slider')) {
  class CardSlider extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.card-slider__track');
      this.slides = Array.from(this.querySelectorAll('.card-slider__slide'));
      this.dots = Array.from(this.querySelectorAll('.card-slider__dot'));
      this.prevButton = this.querySelector('.card-slider__arrow--prev');
      this.nextButton = this.querySelector('.card-slider__arrow--next');
      this.currentIndex = 0;
      this.dragStartX = 0;
      this.dragCurrentX = 0;
      this.isDragging = false;

      if (!this.track || this.slides.length < 2) return;

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.onPrevClick.bind(this));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.onNextClick.bind(this));
      }
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.goToSlide(index);
        });
      });

      this.track.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.track.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd.bind(this));

      // component-card.css raises .card__media's z-index above the card's
      // invisible full-card "stretched link" (a::after) so the arrow/dot
      // buttons above can actually receive clicks instead of the link
      // swallowing them (a plain z-index:2 on the buttons themselves isn't
      // enough: it only wins comparisons *inside* .card__media's own
      // stacking context, and from outside, the whole box only ever counts
      // as .card__media's OWN z-index). That fix necessarily also puts the
      // rest of the image (not just the buttons) above the link, which
      // would otherwise swallow plain "click the photo to open the
      // product" clicks too. Restore that behavior here in JS instead of
      // trying to solve it in CSS: any click landing on the track that
      // isn't on an arrow/dot (those already stopPropagation in their own
      // handlers, so this never double-fires for them) navigates to the
      // product directly. This also covers mobile taps for free — the
      // browser does not synthesize a `click` after a touch gesture that
      // involved real movement, so a genuine swipe never triggers it.
      this.track.addEventListener('click', this.onTrackClick.bind(this));
    }

    onTrackClick(event) {
      if (event.target.closest('.card-slider__arrow, .card-slider__dots')) return;
      const link = this.closest('.card-wrapper, .card')?.querySelector('a.full-unstyled-link');
      if (link && link.href) {
        window.location.href = link.href;
      }
    }

    onPrevClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.goToSlide(this.currentIndex - 1);
    }

    onNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.goToSlide(this.currentIndex + 1);
    }

    goToSlide(index) {
      const lastIndex = this.slides.length - 1;
      if (index < 0) index = lastIndex;
      if (index > lastIndex) index = 0;
      this.currentIndex = index;
      this.track.style.transform = 'translateX(-' + index * 100 + '%)';
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    onTouchStart(event) {
      this.dragStartX = event.touches[0].clientX;
      this.dragCurrentX = this.dragStartX;
      this.isDragging = true;
      this.track.style.transition = 'none';
    }

    onTouchMove(event) {
      if (!this.isDragging) return;
      this.dragCurrentX = event.touches[0].clientX;
      const deltaX = this.dragCurrentX - this.dragStartX;
      const width = this.track.getBoundingClientRect().width || 1;
      const deltaPercent = (deltaX / width) * 100;
      this.track.style.transform = 'translateX(calc(-' + this.currentIndex * 100 + '% + ' + deltaPercent + '%))';
    }

    onTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.style.transition = '';
      const deltaX = this.dragCurrentX - this.dragStartX;
      const SWIPE_THRESHOLD = 35;
      if (deltaX <= -SWIPE_THRESHOLD) {
        this.goToSlide(this.currentIndex + 1);
      } else if (deltaX >= SWIPE_THRESHOLD) {
        this.goToSlide(this.currentIndex - 1);
      } else {
        this.goToSlide(this.currentIndex);
      }
      this.dragStartX = 0;
      this.dragCurrentX = 0;
    }
  }

  customElements.define('card-slider', CardSlider);
}
